import { useMemo, useState } from "react";
import { View, ScrollView, Pressable, Text, Platform, Alert } from "react-native";
import { CheckBox } from "react-native-elements";
import { addDoc, collection, deleteDoc, doc } from "firebase/firestore";
import AdminInput from "../AdminInput";
import AdminAlert from "../AdminAlert";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import { firestore } from "../../services/firebase";
import { PRODUCT_CATEGORIES } from "../../constants/entities";
import { useProductCategories } from "../../queries/useProductCategories";
import { useCatalogProducts } from "../../queries/useCatalogProducts";
import { queryClient } from "../../queries/queryClient";

/**
 * Vincula o produto a uma "prateleira" da vitrine (ex.: "Cursos teóricos",
 * "Formações"). Criada aqui no formulário do produto; pode ser excluída quando
 * nenhum produto da vitrine estiver vinculado a ela.
 */
export function ProductCategoryPicker({ value, onChange, compact = false }) {
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteFeedback, setDeleteFeedback] = useState(null);
  const { data: categories = [], isLoading } = useProductCategories();
  const { data: catalogProducts = [] } = useCatalogProducts();

  const selected = categories.find((category) => category.id === value);

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return categories.filter((category) => !term || category.name.toLowerCase().includes(term));
  }, [categories, search]);

  const exactMatch = categories.some(
    (category) => category.name.trim().toLowerCase() === search.trim().toLowerCase()
  );

  const handleSelect = (category) => {
    onChange(category.id);
    setSearch("");
    setDeleteFeedback(null);
  };

  const handleCreate = async () => {
    const name = search.trim();
    if (!name || creating) return;

    setCreating(true);
    setDeleteFeedback(null);
    try {
      const maxOrder = categories.reduce((max, category) => Math.max(max, category.order ?? 0), 0);
      const docRef = await addDoc(collection(firestore, PRODUCT_CATEGORIES), {
        name,
        order: maxOrder + 1,
        archived: false,
      });
      queryClient.invalidateQueries({ queryKey: [PRODUCT_CATEGORIES] });
      onChange(docRef.id);
      setSearch("");
    } catch (error) {
      console.error("Erro ao criar categoria de produto:", error);
    } finally {
      setCreating(false);
    }
  };

  const getProductsInCategory = (categoryId) =>
    catalogProducts.filter((product) => product.productCategoryId === categoryId);

  const confirmDeleteCategory = (categoryName) =>
    new Promise((resolve) => {
      const message = `Excluir a categoria "${categoryName}"? Esta ação não pode ser desfeita.`;

      if (Platform.OS === "web") {
        resolve(window.confirm(message));
        return;
      }

      Alert.alert("Excluir categoria", message, [
        { text: "Cancelar", style: "cancel", onPress: () => resolve(false) },
        { text: "Excluir", style: "destructive", onPress: () => resolve(true) },
      ]);
    });

  const handleDeleteCategory = async (category) => {
    if (!category?.id || deletingId) return;

    const linkedProducts = getProductsInCategory(category.id);
    if (linkedProducts.length > 0) {
      setDeleteFeedback({
        kind: "blocked",
        categoryName: category.name,
        products: linkedProducts,
      });
      return;
    }

    const confirmed = await confirmDeleteCategory(category.name);
    if (!confirmed) return;

    setDeletingId(category.id);
    setDeleteFeedback(null);
    try {
      await deleteDoc(doc(firestore, PRODUCT_CATEGORIES, category.id));
      if (value === category.id) {
        onChange(null);
      }
      queryClient.invalidateQueries({ queryKey: [PRODUCT_CATEGORIES] });
    } catch (error) {
      console.error("Erro ao excluir categoria de produto:", error);
      setDeleteFeedback({
        kind: "error",
        message: "Não foi possível excluir a categoria. Tente novamente.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <View>
      {selected && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingVertical: 8,
            paddingHorizontal: 12,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.border,
            backgroundColor: ADMIN_COLORS.accentSoft,
            marginBottom: 8,
          }}
        >
          <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: "600", color: ADMIN_COLORS.ink }}>
            {selected.name}
          </Text>
          <Pressable onPress={() => onChange(null)}>
            <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.danger }}>Remover</Text>
          </Pressable>
        </View>
      )}

      <AdminInput
        placeholder="Buscar ou criar categoria..."
        value={search}
        onChangeText={setSearch}
        style={compact ? { fontSize: 13, height: 38, marginTop: 0 } : undefined}
      />

      <ScrollView
        style={{
          maxHeight: 150,
          marginTop: 8,
          borderWidth: 1,
          borderColor: ADMIN_COLORS.border,
          borderRadius: 10,
          backgroundColor: ADMIN_COLORS.surface,
        }}
        nestedScrollEnabled
      >
        {isLoading && (
          <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted, padding: 12 }}>
            Carregando...
          </Text>
        )}
        {!isLoading && candidates.length === 0 && !search.trim() && (
          <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted, padding: 12 }}>
            Nenhuma categoria de produto criada ainda.
          </Text>
        )}
        {candidates.map((category) => {
          const isDeleting = deletingId === category.id;

          return (
            <View
              key={category.id}
              style={{
                flexDirection: "row",
                alignItems: "center",
                borderBottomWidth: 1,
                borderBottomColor: ADMIN_COLORS.border,
              }}
            >
              <View style={{ flex: 1 }}>
                <CheckBox
                  title={category.name}
                  checked={category.id === value}
                  onPress={() => handleSelect(category)}
                  containerStyle={{ backgroundColor: "transparent", paddingVertical: 4, marginRight: 0 }}
                  textStyle={{ fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: "400", color: ADMIN_COLORS.ink }}
                />
              </View>
              <Pressable
                onPress={() => handleDeleteCategory(category)}
                disabled={isDeleting}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  opacity: isDeleting ? 0.4 : 1,
                }}
              >
                <Text style={{ fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.danger }}>
                  {isDeleting ? "..." : "Excluir"}
                </Text>
              </Pressable>
            </View>
          );
        })}
        {search.trim() && !exactMatch && (
          <Pressable
            onPress={handleCreate}
            disabled={creating}
            style={{
              paddingVertical: 10,
              paddingHorizontal: 12,
              borderTopWidth: candidates.length > 0 ? 1 : 0,
              borderTopColor: ADMIN_COLORS.border,
              opacity: creating ? 0.6 : 1,
            }}
          >
            <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: "600", color: ADMIN_COLORS.accent }}>
              {creating ? "Criando..." : `+ Criar categoria "${search.trim()}"`}
            </Text>
          </Pressable>
        )}
      </ScrollView>
      {deleteFeedback?.kind === "blocked" && (
        <View style={{ marginTop: 8 }}>
          <AdminAlert variant="error">
            <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.ink }}>
              Não é possível excluir &quot;{deleteFeedback.categoryName}&quot;.{" "}
              {deleteFeedback.products.length === 1
                ? "Este produto ainda está vinculado:"
                : "Estes produtos ainda estão vinculados:"}
            </Text>
            {deleteFeedback.products.map((product) => (
              <Text
                key={product.id}
                style={{
                  fontFamily: ADMIN_FONT,
                  fontSize: 13,
                  fontWeight: "600",
                  color: ADMIN_COLORS.ink,
                  marginTop: 6,
                }}
              >
                • {product.name || "Produto sem nome"}
              </Text>
            ))}
            <Text
              style={{
                fontFamily: ADMIN_FONT,
                fontSize: 12,
                color: ADMIN_COLORS.muted,
                marginTop: 8,
              }}
            >
              Altere a categoria deles na vitrine antes de excluir.
            </Text>
          </AdminAlert>
        </View>
      )}
      {deleteFeedback?.kind === "error" && (
        <View style={{ marginTop: 8 }}>
          <AdminAlert variant="error">{deleteFeedback.message}</AdminAlert>
        </View>
      )}
    </View>
  );
}

export default ProductCategoryPicker;
